<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue'

import { describeError, logger } from '@/lib/log'
import {
  awaitFirstPresentedFrame,
  decodeImage,
  mediaErrorMessage,
  prepareVideo,
  releaseVideo,
} from '@/lib/mediaElement'
import type { LayerStatus, MediaFit, MediaItem } from '@/types'

const props = defineProps<{
  /** Fully opaque when true. Idle layers stay in the DOM at `opacity: 0`. */
  visible: boolean
  /** Stacked above the outgoing layer so a crossfade never dips through black. */
  elevated: boolean
  transitionMs: number
  muted: boolean
  fitOverride: MediaFit | null
  prepareTimeoutMs: number
  firstFrameTimeoutMs: number
}>()

const imageEl = ref<HTMLImageElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)

const item = shallowRef<MediaItem | null>(null)
const ready = ref(false)
const error = ref<string | null>(null)

/** Suppresses the `error` event that tearing down a video source can raise. */
let releasing = false

const fit = computed<MediaFit>(() => props.fitOverride ?? item.value?.fit ?? 'cover')

const layerStyle = computed(() => ({
  opacity: props.visible ? '1' : '0',
  zIndex: props.elevated ? '2' : '1',
  // Only the incoming layer animates; retiring back to hidden must be instant
  // because it happens underneath a fully opaque layer.
  transitionDuration: props.visible ? `${props.transitionMs}ms` : '0ms',
}))

function onVideoError(): void {
  if (releasing) return
  const element = videoEl.value
  error.value = element ? mediaErrorMessage(element) : 'Video error'
  ready.value = false
}

/**
 * Loads media and resolves only once a frame is decoded and ready to present.
 * Rejects so the caller can drop a broken item from the rotation.
 */
async function prepare(next: MediaItem, seekMs: number): Promise<void> {
  error.value = null
  ready.value = false

  try {
    if (next.kind === 'image') {
      const element = imageEl.value
      if (!element) throw new Error('Image element is not mounted')
      await decodeImage(element, next.src, props.prepareTimeoutMs)
    } else {
      const element = videoEl.value
      if (!element) throw new Error('Video element is not mounted')
      releasing = false
      await prepareVideo(element, {
        src: next.src,
        seekMs,
        loop: next.loop,
        muted: props.muted,
        poster: next.poster,
        timeoutMs: props.prepareTimeoutMs,
      })
    }
  } catch (caught) {
    error.value = describeError(caught)
    throw caught
  }

  item.value = next
  ready.value = true
}

/**
 * Starts playback while the layer is still invisible and waits for the first
 * presented frame. Never rejects — showing something is always better than
 * blocking the rotation.
 */
async function beginPlayback(): Promise<void> {
  const current = item.value
  if (!current || current.kind !== 'video') return
  const element = videoEl.value
  if (!element) return

  try {
    await element.play()
  } catch (caught) {
    if (!element.muted) {
      logger.warn('Unmuted autoplay was blocked, falling back to muted playback')
      element.muted = true
      try {
        await element.play()
      } catch (retry) {
        error.value = describeError(retry)
        logger.error(`Playback failed for ${current.id}`, retry)
      }
    } else {
      error.value = describeError(caught)
      logger.error(`Playback failed for ${current.id}`, caught)
    }
  }

  await awaitFirstPresentedFrame(element, props.firstFrameTimeoutMs)
}

function release(): void {
  ready.value = false
  error.value = null
  const video = videoEl.value
  if (video && video.src !== '') {
    releasing = true
    releaseVideo(video)
    releasing = false
  }
  const image = imageEl.value
  if (image) image.removeAttribute('src')
  item.value = null
}

function status(): LayerStatus {
  const current = item.value
  const video = current?.kind === 'video' ? videoEl.value : null
  return {
    itemId: current?.id ?? null,
    kind: current?.kind ?? null,
    ready: ready.value,
    error: error.value,
    currentTimeMs: video ? Math.round(video.currentTime * 1_000) : 0,
    durationMs: video && Number.isFinite(video.duration) ? Math.round(video.duration * 1_000) : 0,
    paused: video ? video.paused : true,
    ended: video ? video.ended : false,
  }
}

defineExpose({ prepare, beginPlayback, release, status })
</script>

<template>
  <div class="layer" :style="layerStyle">
    <img
      ref="imageEl"
      class="media"
      :class="{ shown: item?.kind === 'image' }"
      :style="{ objectFit: fit }"
      alt=""
      draggable="false"
    />
    <!-- Kept mounted at all times: creating a media element mid-transition is
         exactly what produces the black flash this player avoids. -->
    <video
      ref="videoEl"
      class="media"
      :class="{ shown: item?.kind === 'video' }"
      :style="{ objectFit: fit }"
      muted
      playsinline
      preload="auto"
      disablepictureinpicture
      @error="onVideoError"
    ></video>
  </div>
</template>

<style scoped>
.layer {
  position: absolute;
  inset: 0;
  /* Opaque so the incoming layer fully covers the outgoing one once faded in. */
  background: #000;
  opacity: 0;
  transition-property: opacity;
  transition-timing-function: linear;
  /* Promote to its own compositor layer up front, so the fade never triggers a
     layer creation (and repaint) at the moment the transition starts. */
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-position: center;
  /* Never use display/visibility here — hidden media elements can have their
     decoding suspended, which reintroduces the flash on reveal. */
  opacity: 0;
  pointer-events: none;
}

.media.shown {
  opacity: 1;
}
</style>
